import type { TimePeriod } from './dashboard'
import { periodToMs } from './dashboard'

export interface TimeSeriesPoint {
  label: string       // X-axis label ("Feb 24" or "14:00")
  bucketStart: string // ISO timestamp for sorting
  value: number
}

export interface DashboardTimeSeries {
  revenue: TimeSeriesPoint[]   // Protocol revenue per bucket (SOL)
  launches: TimeSeriesPoint[]  // Token launch count per bucket
  volume: TimeSeriesPoint[]    // Trading volume per bucket (SOL)
  activeTokens: TimeSeriesPoint[] // Unique tokens with trades per bucket
}

interface BucketConfig {
  bucketMs: number
  bucketCount: number
  formatLabel: (date: Date) => string
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function dayLabel(d: Date): string {
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`
}

function weekLabel(d: Date): string {
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`
}

export function getBucketConfig(period: TimePeriod): BucketConfig {
  const hourMs = 60 * 60 * 1000
  const dayMs = 24 * hourMs
  const weekMs = 7 * dayMs

  switch (period) {
    case '24h':
      return {
        bucketMs: hourMs,
        bucketCount: 24,
        formatLabel: (d: Date) =>
          `${d.getHours().toString().padStart(2, '0')}:00`,
      }
    case '7d':
      return {
        bucketMs: dayMs,
        bucketCount: 7,
        formatLabel: dayLabel,
      }
    case '30d':
      return {
        bucketMs: dayMs,
        bucketCount: 30,
        formatLabel: dayLabel,
      }
    case 'all': {
      // Dynamic: compute range from platform epoch to now
      const totalMs = periodToMs('all')
      const totalDays = Math.ceil(totalMs / dayMs)
      // Use weekly buckets if > 90 days, daily otherwise
      if (totalDays > 90) {
        const bucketCount = Math.ceil(totalDays / 7)
        return { bucketMs: weekMs, bucketCount, formatLabel: weekLabel }
      }
      return { bucketMs: dayMs, bucketCount: totalDays, formatLabel: dayLabel }
    }
  }
}

export function createEmptyBuckets(period: TimePeriod): TimeSeriesPoint[] {
  const config = getBucketConfig(period)
  const now = Date.now()
  const pMs = periodToMs(period)
  const periodStart = now - pMs

  const buckets: TimeSeriesPoint[] = []
  for (let i = 0; i < config.bucketCount; i++) {
    const bucketStart = new Date(periodStart + i * config.bucketMs)
    buckets.push({
      label: config.formatLabel(bucketStart),
      bucketStart: bucketStart.toISOString(),
      value: 0,
    })
  }
  return buckets
}

export function getBucketIndex(
  timestampMs: number,
  periodStartMs: number,
  bucketMs: number,
  bucketCount: number
): number {
  const offset = timestampMs - periodStartMs
  if (offset < 0) return -1
  const idx = Math.floor(offset / bucketMs)
  if (idx >= bucketCount) return -1
  return idx
}
